import React, { useMemo, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import 'chart.js/auto';
import { TableCellsIcon, ChartBarIcon } from '@heroicons/react/24/outline';

/**
 * Volumen diario de correo del canal AR.
 *
 * **Por qué una pila y no cuatro líneas.** Enviados, entregados y rebotados no
 * son tres medidas independientes: los dos últimos son desenlaces de la primera.
 * Apilándolos, la altura de la barra *es* lo enviado y el trozo rojo *es* lo que
 * rebotó ese día, que es justo la lectura que se busca. Cuatro líneas sueltas
 * obligarían a restar a ojo.
 *
 * **Por qué existe «sin confirmar».** Es `enviados − entregados − rebotados`: lo
 * que Resend aceptó y de lo que todavía no sabemos el desenlace. Hoy es *todo* el
 * volumen, porque quien escribe esos contadores es el webhook de eventos y eso
 * es trabajo de la etapa 5. Sin ese segmento el gráfico saldría vacío y
 * parecería que no se envía nada.
 *
 * **Aperturas va en línea y no en la pila** porque no compite con los otros: un
 * correo abierto ya estaba entregado, así que sumarlo a la pila contaría dos
 * veces el mismo envío. Comparte eje —son correos, misma unidad y misma escala—,
 * que no es lo mismo que meter un segundo eje: eso sí estaría mal.
 */

// Paleta validada contra fondo blanco: todos los pares quedan por encima del
// suelo de separación, también en visión daltónica. Verde/rojo para
// entregado/rebotado se descartó porque para un deuteránope son el mismo color
// (ΔE 4,1) y distinguirlos es justo lo único que hay que poder hacer aquí.
// El gris de «sin confirmar» es acromático a propósito: gris = no lo sabemos.
const COLOR = {
  entregados: '#2a78d6',
  rebotados: '#d03b3b',
  sinConfirmar: '#898781',
  aperturas: '#4a3aa7',
};

const SUPERFICIE = '#ffffff';
const TINTA_TENUE = '#64748b';
const REJILLA = '#e2e8f0';

const nf = new Intl.NumberFormat('es-ES');

const etiquetaDia = iso => {
  const [y, m, d] = iso.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
  });
};

/** Porcentaje sobre lo enviado. Sin envíos no hay ratio: un 0% sería mentira. */
const ratio = (parte, total) => (total > 0 ? `${((parte / total) * 100).toFixed(1)}%` : '—');

const Cifra = ({ etiqueta, valor, pie, color }) => (
  <div className='bg-white border border-slate-200 rounded-lg px-3 py-2'>
    <div className='flex items-center gap-1.5'>
      {color && <span className='w-2 h-2 rounded-lg shrink-0' style={{ backgroundColor: color }} aria-hidden='true' />}
      <p className='text-xs text-slate-500'>{etiqueta}</p>
    </div>
    <p className='text-lg font-semibold text-slate-900 leading-tight'>{nf.format(valor)}</p>
    <p className='text-xs text-slate-400'>{pie}</p>
  </div>
);

const barraApilada = (label, valores, color) => ({
  type: 'bar',
  label,
  data: valores,
  backgroundColor: color,
  stack: 'desenlace',
  borderRadius: 4,
  borderSkipped: 'bottom',
  // 2px de superficie entre segmentos: separa la pila sin meter una línea que
  // parezca un dato más.
  borderColor: SUPERFICIE,
  borderWidth: { top: 2 },
  barPercentage: 0.8,
  categoryPercentage: 0.85,
});

const EmailVolumeChart = ({ stats, isLoading }) => {
  const [verTabla, setVerTabla] = useState(false);

  const dias = useMemo(() => stats?.days || [], [stats]);
  const totals = stats?.totals || {};
  const enviados = totals.sent || 0;

  const data = useMemo(
    () => ({
      labels: dias.map(d => etiquetaDia(d.date)),
      datasets: [
        barraApilada(
          'Entregados',
          dias.map(d => d.delivered),
          COLOR.entregados
        ),
        barraApilada(
          'Rebotados',
          dias.map(d => d.bounced),
          COLOR.rebotados
        ),
        barraApilada(
          'Sin confirmar',
          dias.map(d => d.unconfirmed),
          COLOR.sinConfirmar
        ),
        {
          type: 'line',
          label: 'Aperturas',
          data: dias.map(d => d.opened),
          borderColor: COLOR.aperturas,
          backgroundColor: COLOR.aperturas,
          borderWidth: 2,
          tension: 0.25,
          // Con una ventana larga los puntos se pisan; en una corta ayudan a ver
          // dónde cae cada día.
          pointRadius: dias.length <= 14 ? 4 : 0,
          pointHoverRadius: 5,
          // Anillo de superficie para que el punto no se funda con la barra.
          pointBorderColor: SUPERFICIE,
          pointBorderWidth: 2,
        },
      ],
    }),
    [dias]
  );

  const options = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { usePointStyle: true, pointStyle: 'circle', padding: 16, font: { size: 12 } },
        },
        tooltip: {
          backgroundColor: '#1e293b',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          padding: 12,
          cornerRadius: 8,
          callbacks: {
            label: ctx => `${ctx.dataset.label}: ${nf.format(ctx.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          border: { display: false },
          ticks: { color: TINTA_TENUE, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 12 },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: REJILLA, drawBorder: false },
          border: { display: false },
          ticks: { color: TINTA_TENUE, font: { size: 11 }, precision: 0 },
        },
      },
    }),
    []
  );

  // Solo la primera carga vacía la pantalla. Al cambiar de empresa o de ventana
  // se atenúa lo anterior y se deja en su sitio: si se sustituyera por un
  // spinner, el bloque entero cambiaría de alto y la tabla de abajo daría un
  // salto en cada cambio de filtro.
  if (isLoading && !stats) {
    return (
      <div className='bg-white border border-slate-200 rounded-lg h-64 flex items-center justify-center'>
        <div className='animate-spin rounded-lg h-6 w-6 border-b-2 border-blue-500'></div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 transition-opacity ${isLoading ? 'opacity-50' : ''}`} aria-busy={isLoading}>
      <div className='grid grid-cols-2 md:grid-cols-5 gap-2'>
        <Cifra etiqueta='Enviados' valor={enviados} pie={`${dias.length} días`} />
        <Cifra
          etiqueta='Entregados'
          valor={totals.delivered || 0}
          pie={ratio(totals.delivered, enviados)}
          color={COLOR.entregados}
        />
        <Cifra
          etiqueta='Rebotados'
          valor={totals.bounced || 0}
          pie={ratio(totals.bounced, enviados)}
          color={COLOR.rebotados}
        />
        <Cifra etiqueta='Quejas' valor={totals.complained || 0} pie={ratio(totals.complained, enviados)} />
        <Cifra
          etiqueta='Aperturas'
          valor={totals.opened || 0}
          pie={ratio(totals.opened, enviados)}
          color={COLOR.aperturas}
        />
      </div>

      <div className='bg-white border border-slate-200 rounded-lg p-4'>
        <div className='flex items-start justify-between gap-3 mb-3'>
          <div>
            <h2 className='text-sm font-medium text-slate-800'>Envíos por día</h2>
            <p className='text-xs text-slate-500'>
              La altura de cada barra es lo enviado ese día, partido por cómo acabó.
            </p>
          </div>
          <button
            type='button'
            onClick={() => setVerTabla(v => !v)}
            className='shrink-0 inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800'
          >
            {verTabla ? <ChartBarIcon className='w-4 h-4' /> : <TableCellsIcon className='w-4 h-4' />}
            {verTabla ? 'Ver gráfico' : 'Ver tabla'}
          </button>
        </div>

        {enviados === 0 ? (
          <div className='h-40 flex flex-col items-center justify-center text-center'>
            <p className='text-sm text-slate-500'>Todavía no se ha enviado ningún correo por AR en esta ventana.</p>
            <p className='text-xs text-slate-400 mt-1'>
              El contador empieza en el primer envío desde un expediente de AR.
            </p>
          </div>
        ) : verTabla ? (
          <div className='overflow-x-auto max-h-72'>
            <table className='w-full text-sm'>
              <thead className='sticky top-0 bg-white'>
                <tr className='border-b border-slate-200 text-left'>
                  <th className='px-3 py-2 text-xs font-medium text-slate-500'>Día</th>
                  <th className='px-3 py-2 text-xs font-medium text-slate-500 text-right'>Enviados</th>
                  <th className='px-3 py-2 text-xs font-medium text-slate-500 text-right'>Entregados</th>
                  <th className='px-3 py-2 text-xs font-medium text-slate-500 text-right'>Rebotados</th>
                  <th className='px-3 py-2 text-xs font-medium text-slate-500 text-right'>Quejas</th>
                  <th className='px-3 py-2 text-xs font-medium text-slate-500 text-right'>Aperturas</th>
                </tr>
              </thead>
              <tbody>
                {dias
                  .filter(d => d.sent > 0)
                  .map(d => (
                    <tr key={d.date} className='border-b border-slate-100 last:border-b-0'>
                      <td className='px-3 py-2 text-slate-700'>{etiquetaDia(d.date)}</td>
                      <td className='px-3 py-2 text-right text-slate-900'>{nf.format(d.sent)}</td>
                      <td className='px-3 py-2 text-right text-slate-600'>{nf.format(d.delivered)}</td>
                      <td className='px-3 py-2 text-right text-slate-600'>{nf.format(d.bounced)}</td>
                      <td className='px-3 py-2 text-right text-slate-600'>{nf.format(d.complained)}</td>
                      <td className='px-3 py-2 text-right text-slate-600'>{nf.format(d.opened)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className='h-64'>
            <Chart type='bar' data={data} options={options} />
          </div>
        )}

        {enviados > 0 && (totals.delivered || 0) + (totals.bounced || 0) === 0 && (
          <p className='text-xs text-slate-500 mt-3 border-t border-slate-100 pt-3'>
            Todo sale como «sin confirmar» porque quien anota entregas, rebotes y aperturas es el webhook de eventos de
            Resend, que todavía no está montado (etapa 5 del plan). Lo que sí es real es la columna de enviados.
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailVolumeChart;
